import type {
  ApiCallback,
  ApiObject,
  ApiOptions,
  IdentifyTraits,
  RudderAnalytics,
} from '@rudderstack/analytics-js';
import type { ClientEnvironmentConfig } from '@core/config-models';

/**
 * Patches the rudderstack client to provide App, Segment and Environment data.
 * This data is useful for partitioning in downstream systems as it gives hints about what type of environment and app
 * we are visiting from, instead of relying on URL conventions.
 *
 * @param rudderstack Rudderstack SDK instance.
 * @param config Environment Configuration for this application.
 * @returns A patched version of Rudderstack's methods that provides contextual clues.
 */
export const decorateRudderstack = (
  rudderstack: RudderAnalytics,
  config: ClientEnvironmentConfig
) => {
  const originalTrack = rudderstack.track;
  const originalIdentify = rudderstack.identify;
  const originalPage = rudderstack.page;

  /**
   * Helper to merge custom metadata into an options object
   */
  const mergeCustomOptions = (options?: ApiOptions | null): ApiOptions => ({
    ...options,
    webContext: {
      app: config.app,
      segment: config.segment,
      environment: config.environment,
    },
  });

  /**
   * Patch rudderstack.track
   */
  rudderstack.track = function (...args: any[]) {
    const event: string = args[0];
    let properties: ApiObject | null | undefined;
    let options: ApiOptions | undefined;
    let callback: ApiCallback | undefined;

    if (typeof args[1] === 'function') {
      callback = args[1];
    } else if (typeof args[2] === 'function') {
      properties = args[1];
      callback = args[2];
    } else {
      properties = args[1];
      options = args[2];
      callback = args[3];
    }

    const finalOptions = mergeCustomOptions(options);

    // Cast to any because TypeScript cannot verify overload tuple spreads
    return (originalTrack as any)(event, properties, finalOptions, callback);
  } as RudderAnalytics['track'];

  /**
   * Patch rudderstack.identify
   */
  rudderstack.identify = function (...args: any[]) {
    let userId: string | undefined;
    let traits: IdentifyTraits | null | undefined;
    let options: ApiOptions | undefined;
    let callback: ApiCallback | undefined;

    if (typeof args[0] === 'string') {
      // First argument is userId
      userId = args[0];

      if (typeof args[1] === 'function') {
        callback = args[1];
      } else if (typeof args[2] === 'function') {
        traits = args[1];
        callback = args[2];
      } else {
        traits = args[1];
        options = args[2];
        callback = args[3];
      }
    } else {
      // First argument is traits
      traits = args[0];

      if (typeof args[1] === 'function') {
        callback = args[1];
      } else {
        options = args[1];
        callback = args[2];
      }
    }

    const finalOptions = mergeCustomOptions(options);

    // Cast to any because TypeScript cannot verify overload tuple spreads
    return (originalIdentify as any)(userId, traits, finalOptions, callback);
  } as RudderAnalytics['identify'];

  /**
   * Patch rudderstack.page
   */
  // eslint-disable-next-line complexity
  rudderstack.page = function (...args: any[]) {
    let category: string | undefined;
    let name: string | undefined;
    let properties: ApiObject | null | undefined;
    let options: ApiOptions | undefined;
    let callback: ApiCallback | undefined;

    if (typeof args[0] === 'string' && typeof args[1] === 'string') {
      // page(category, name, ...)
      category = args[0];
      name = args[1];
      if (typeof args[2] === 'function') {
        callback = args[2];
      } else if (typeof args[3] === 'function') {
        properties = args[2];
        callback = args[3];
      } else {
        properties = args[2];
        options = args[3];
        callback = args[4];
      }
    } else if (typeof args[0] === 'string') {
      // page(name, ...)
      name = args[0];
      if (typeof args[1] === 'function') {
        callback = args[1];
      } else if (typeof args[2] === 'function') {
        properties = args[1];
        callback = args[2];
      } else {
        properties = args[1];
        options = args[2];
        callback = args[3];
      }
    } else if (typeof args[0] === 'object') {
      // page(properties, ...)
      properties = args[0];
      if (typeof args[1] === 'function') {
        callback = args[1];
      } else {
        options = args[1];
        callback = args[2];
      }
    } else if (typeof args[0] === 'function') {
      // page(callback)
      callback = args[0];
    }

    const finalOptions = mergeCustomOptions(options);
    return (originalPage as any)(
      category,
      name,
      properties,
      finalOptions,
      callback
    );
  } as RudderAnalytics['page'];

  return rudderstack;
};
